// ═══════════════════════════════════════════════════════════════════════════
//  PASANG TV / TABLET KAMAR LEWAT LOGIN STAF (2 Okt 2026)
//
//  Zaldy: "kenapa masih pakai kode, susah — bikin otomatis aja kalau booking
//  start kebuka ruangan di TV sama tab, pakai login lah".
//
//  Staf login SEKALI di perangkat itu (akun karaoke), pilih room, selesai.
//  Token disimpan di perangkat; sejak itu begitu booking ElPOS di room itu
//  dimulai, sesi dibuka otomatis dan layar ini terbuka sendiri — tanpa kode.
//
//  Staf-only, jadi berbahasa Indonesia saja.
// ═══════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react'
import axios from 'axios'
import { Loader2, AlertTriangle, Tv, Tablet, Search, Check } from 'lucide-react'

type Room = { id: string; nama: string; tipe: string; outlet: string; jml_tv: number; jml_tablet: number }

const pesan = (e: unknown) => {
  const ax = e as { response?: { data?: { error?: string } }; message?: string }
  return ax?.response?.data?.error || ax?.message || 'Gagal menghubungi server'
}

export default function PasangLogin({ jenis, kunci, sesudah, batal }: {
  jenis: 'tv' | 'tablet'
  kunci: string            // kunci localStorage tempat token disimpan
  sesudah: () => void      // dipanggil sesudah token tersimpan
  batal?: () => void
}) {
  const [ident, setIdent] = useState('')
  const [sandi, setSandi] = useState('')
  const [rooms, setRooms] = useState<Room[] | null>(null)
  const [cari, setCari] = useState('')
  const [sibuk, setSibuk] = useState(false)
  const [galat, setGalat] = useState('')
  const tv = jenis === 'tv'

  async function masuk() {
    if (!ident.trim() || !sandi) { setGalat('Isi email/nomor dan kata sandi.'); return }
    setSibuk(true); setGalat('')
    try {
      const r = await axios.post<{ rooms: Room[] }>('/auth/perangkat', { identitas: ident.trim(), password: sandi })
      setRooms(r.data.rooms ?? [])
    } catch (e) { setGalat(pesan(e)) } finally { setSibuk(false) }
  }

  async function pilih(room: Room) {
    setSibuk(true); setGalat('')
    try {
      const r = await axios.post<{ token: string }>('/auth/perangkat', {
        identitas: ident.trim(), password: sandi, room_id: room.id, jenis,
      })
      try { localStorage.setItem(kunci, r.data.token) } catch {
        setGalat('Perangkat ini tidak bisa menyimpan data (mode privat?). Buka di browser biasa.')
        return
      }
      setSandi('')
      sesudah()
    } catch (e) { setGalat(pesan(e)) } finally { setSibuk(false) }
  }

  const tampil = useMemo(() => {
    const q = cari.trim().toLowerCase()
    return (rooms ?? []).filter(r => !q || `${r.nama} ${r.outlet} ${r.tipe}`.toLowerCase().includes(q))
  }, [rooms, cari])

  const Ikon = tv ? Tv : Tablet

  return (
    <div className="min-h-screen grid place-items-center px-5 py-8 text-text"
         style={{ background: 'linear-gradient(180deg, #0b0e22 0%, #0d1130 50%, #0a0c1c 100%)' }}>
      <div className="w-full max-w-lg">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl grid place-items-center bg-gold/10 border border-gold/25 text-gold">
            <Ikon size={26} />
          </div>
          <h1 className="mt-4 text-xl font-semibold">{tv ? 'Pasang TV kamar' : 'Pasang tablet kamar'}</h1>
          <p className="mt-1 text-[13px] text-text-3 leading-relaxed">
            {rooms === null
              ? 'Login dengan akun staf karaoke (kapten / admin). Cukup sekali per perangkat.'
              : `Pilih room tempat ${tv ? 'TV' : 'tablet'} ini dipasang.`}
          </p>
        </div>

        {rooms === null ? (
          <div className="mt-6 space-y-3">
            <input value={ident} onChange={e => setIdent(e.target.value)} placeholder="Email atau nomor HP"
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              className="w-full bg-bg-1 border border-border rounded-2xl px-4 py-3.5 outline-none focus:border-gold/50" />
            <input value={sandi} onChange={e => setSandi(e.target.value)} placeholder="Kata sandi" type="password"
              onKeyDown={e => e.key === 'Enter' && masuk()}
              className="w-full bg-bg-1 border border-border rounded-2xl px-4 py-3.5 outline-none focus:border-gold/50" />
            <button onClick={masuk} disabled={sibuk}
              className="w-full py-3.5 rounded-2xl bg-gold text-black font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
              {sibuk && <Loader2 size={17} className="animate-spin" />} Masuk
            </button>
          </div>
        ) : (
          <div className="mt-5">
            <div className="relative">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-3" />
              <input value={cari} onChange={e => setCari(e.target.value)} placeholder="Cari room / outlet"
                className="w-full bg-bg-1 border border-border rounded-2xl pl-10 pr-4 py-3 outline-none focus:border-gold/50" />
            </div>
            <div className="mt-3 max-h-[55vh] overflow-y-auto space-y-2 pr-1">
              {tampil.length === 0 && <div className="text-[13px] text-text-3 text-center py-6">Tidak ada room.</div>}
              {tampil.map(r => {
                const sudah = tv ? r.jml_tv : r.jml_tablet
                return (
                  <button key={r.id} onClick={() => pilih(r)} disabled={sibuk}
                    className="w-full text-left rounded-2xl bg-white/[0.04] border border-white/10 px-4 py-3
                               hover:border-gold/40 disabled:opacity-50 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{r.nama}</div>
                      <div className="text-[12px] text-text-3 truncate">{r.outlet}{r.tipe ? ` · ${r.tipe}` : ''}</div>
                    </div>
                    {sudah > 0 && (
                      <span className="shrink-0 text-[11px] text-emerald-300 flex items-center gap-1">
                        <Check size={13} /> {sudah} {tv ? 'TV' : 'tablet'} terpasang
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
            <button onClick={() => { setRooms(null); setCari('') }}
              className="mt-3 text-[12px] text-text-3 underline underline-offset-4">Ganti akun</button>
          </div>
        )}

        {galat && (
          <div className="mt-3 text-[12.5px] text-red-400 flex items-start gap-1.5">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{galat}</span>
          </div>
        )}
        {batal && (
          <div className="mt-6 text-center">
            <button onClick={batal} className="text-[12px] text-text-3 underline underline-offset-4">Kembali</button>
          </div>
        )}
      </div>
    </div>
  )
}
